import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { BaseApiService, ApiResponse } from "./baseApi";

export interface EndgoodCategory {
  id: number;
  categoryName: string;
  categoryCode: string;
  description: string;
  isActive: boolean;
  createdBy: number;
  updatedBy: number;
  createdAt: string;
  updatedAt: string;
}

export interface EndgoodCategoryCreateRequest {
  categoryName: string;
  description?: string;
  isActive: boolean;
}

export interface EndgoodCategoryUpdateRequest extends EndgoodCategoryCreateRequest {
  id: number;
}

class EndgoodCategoryApiService extends BaseApiService {
  // Get all endgood categories
  async getEndgoodCategories(): Promise<EndgoodCategory[]> {
    try {
      const response = await this.makeRequest<ApiResponse<EndgoodCategory[]>>(API_ENDPOINTS.ENDGOOD_CATEGORIES.LIST);
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch endgood categories');
    } catch (error) {
      toast.error('Failed to fetch endgood categories');
      throw error;
    }
  }

  // Get endgood category by ID
  async getEndgoodCategoryById(id: string): Promise<EndgoodCategory> {
    try {
      const response = await this.makeRequest<ApiResponse<EndgoodCategory>>(API_ENDPOINTS.ENDGOOD_CATEGORIES.DETAIL(id));
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch endgood category details');
    } catch (error) {
      toast.error('Failed to fetch endgood category details');
      throw error;
    }
  }

  // Create new endgood category
  async createEndgoodCategory(categoryData: EndgoodCategoryCreateRequest): Promise<EndgoodCategory> {
    try {
      const response = await this.makeRequest<ApiResponse<EndgoodCategory>>(API_ENDPOINTS.ENDGOOD_CATEGORIES.CREATE, {
        method: HTTP_METHODS.POST,
        body: JSON.stringify(categoryData),
      });
      if (response.success) {
        toast.success(response.message || 'Endgood category created successfully!');
        return response.data;
      }
      throw new Error(response.message || 'Failed to create endgood category');
    } catch (error) {
      toast.error('Failed to create endgood category');
      throw error;
    }
  }

  // Update endgood category
  async updateEndgoodCategory(id: string, categoryData: EndgoodCategoryUpdateRequest): Promise<EndgoodCategory> {
    try {
      const response = await this.makeRequest<ApiResponse<EndgoodCategory>>(API_ENDPOINTS.ENDGOOD_CATEGORIES.UPDATE(id), {
        method: HTTP_METHODS.PUT,
        body: JSON.stringify(categoryData),
      });
      if (response.success) {
        toast.success(response.message || 'Endgood category updated successfully!');
        return response.data;
      }
      throw new Error(response.message || 'Failed to update endgood category');
    } catch (error) {
      toast.error('Failed to update endgood category');
      throw error;
    }
  }

  // Delete endgood category
  async deleteEndgoodCategory(id: string): Promise<void> {
    try {
      const response = await this.makeRequest<ApiResponse<unknown>>(API_ENDPOINTS.ENDGOOD_CATEGORIES.DELETE(id), {
        method: HTTP_METHODS.DELETE,
      });
      if (response?.success) {
        toast.success(response.message || 'Endgood category deleted successfully!');
      } else {
        throw new Error(response?.message || 'Failed to delete endgood category');
      }
    } catch (error) {
      toast.error('Failed to delete endgood category');
      throw error;
    }
  }
}

export const endgoodCategoryApiService = new EndgoodCategoryApiService();
